import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { storageService } from "@/services/storageService";
import { IMAGE_UPLOAD_MAX_MB } from "@/lib/compressImage";

export default function ImageUploadField({
  label = "Photo",
  value,
  onChange,
  nameHint = "bearer",
  testId,
  onUpload,
  folderLabel = "office_bearers",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const previousUrl = value;
      const url = onUpload
        ? await onUpload(file, nameHint)
        : await storageService.uploadOfficeBearerImage(file, nameHint);
      if (previousUrl && previousUrl !== url && storageService.isManagedR2Url(previousUrl)) {
        await storageService.deleteR2Url(previousUrl);
      }
      onChange(url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    if (!value) return;
    setUploading(true);
    try {
      if (storageService.isManagedR2Url(value)) {
        await storageService.deleteR2Url(value);
      }
      onChange("");
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err.message || "Could not delete image from storage");
    } finally {
      setUploading(false);
    }
  }

  function onInputChange(e) {
    const file = e.target.files?.[0];
    handleFile(file);
    e.target.value = "";
  }

  return (
    <div data-testid={testId}>
      <label className="label-bcs">{label}</label>
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="shrink-0">
          {value ? (
            <img
              src={value}
              alt=""
              className="h-20 w-20 rounded-xl object-cover border border-gray-200 shadow-sm bg-cream"
            />
          ) : (
            <div className="h-20 w-20 rounded-xl border border-dashed border-gray-300 bg-cream flex items-center justify-center text-ink-muted text-xs text-center px-2">
              No photo
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={onInputChange}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-ink hover:bg-cream transition-colors disabled:opacity-60"
            data-testid={testId ? `${testId}-btn` : undefined}
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {uploading ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
          </button>
          {value && (
            <button
              type="button"
              disabled={uploading}
              onClick={handleRemove}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
              data-testid={testId ? `${testId}-remove` : undefined}
            >
              <X size={15} />
              Remove
            </button>
          )}
        </div>
      </div>
      <p className="mt-1.5 text-xs text-ink-muted">
        Saved to Cloudflare R2 · {folderLabel} · max {IMAGE_UPLOAD_MAX_MB} MB · large images compressed automatically
      </p>
    </div>
  );
}
